/**
 * BLUF-1085 — single source of truth for every SPA URL the app
 * navigates to.
 *
 * ## Why this exists
 *
 * Historically nav paths were hard-coded string literals scattered
 * across 68 call sites in `ui/src/`. That produced two real problems:
 *   1. Typos landed silently. Clicking a `/quoter` link (real bug,
 *      pre-BLUF-1085) rendered a blank page because no route matched.
 *   2. Prefix renames were manual multi-file greps. BLUF-1393
 *      renamed `/admin/*` → `/product-owner/*` and hit a dozen call
 *      sites; had these constants existed, one edit would have caught
 *      every one at compile time.
 *
 * ## Conventions
 *
 * - **Static path constants only.** `QUOTE_HOME = '/quote/quote'`.
 *   No URL builder functions. Parameterised routes
 *   (`/eapp/application-details/:id`, `/portal/clients/:id`, etc.)
 *   stay as inline template strings at their call sites, but build
 *   the prefix from a module base constant so the prefix stays
 *   greppable:
 *     ```
 *     navigate(`${ROUTE.PORTAL}/clients/${clientId}`)
 *     ```
 * - **Module bases exported separately** so the BLUF-1393 `basePath`
 *   pattern (same component renders under `/uw/*` and
 *   `/new-business/*` depending on the caller) reads from constants
 *   too:
 *     ```
 *     <UWQueue basePath={ROUTE.NEW_BUSINESS} />
 *     ```
 * - **Kebab-case values, SCREAMING_SNAKE_CASE keys.** No path
 *   composition helpers. Two constants that share a prefix are two
 *   constants.
 *
 * ## What NOT to put here
 *
 * - Parameterised routes with `:params` — they can't be static
 *   constants and get inlined at the call site using the module
 *   base above.
 * - Fragment identifiers / query strings — those are per-call
 *   concerns; append inline.
 *
 * ## Backend API endpoints (`API_ROUTE`)
 *
 * Backend `/api/*` endpoints referenced from UI code (via `apiFetch`,
 * `apiPath` props, or URL-template building) live in `API_ROUTE`
 * below — same rules, separate namespace. Kept in this file so any
 * URL literal introduced by UI code has one place to look.
 * Parameterised segments (`:id`, statement details) stay inline off
 * the base: `` `${API_ROUTE.PORTAL_COMMISSION_STATEMENTS}/${id}/details` ``.
 *
 * ## Add a new nav target
 *
 * 1. Add a new entry here.
 * 2. Import at the call site: `import { ROUTE } from '.../lib/routes.js'`.
 * 3. Reference as `ROUTE.YOUR_KEY`.
 *
 * TypeScript will catch typos at build time — `ROUTE.QUOTR_HOME`
 * fails compilation, so the `/quoter` class of bug can't recur.
 */

export const ROUTE = {
    // ── Root / landing ──────────────────────────────────────────
    ROOT: '/',

    // ── Auth / login ───────────────────────────────────────────
    /** Backend login redirect. Note: `/api/*` — not part of the SPA. */
    LOGIN: '/api/auth/login',
    AUTH_SELECT_PRODUCER: '/auth/select-producer',
    AUTH_SELECT_ROLE: '/auth/select-role',
    AUTH_NO_PRODUCER: '/auth/no-producer',
    AUTH_ERROR: '/auth/error',

    // ── Module bases (BLUF-1393 basePath pattern) ──────────────
    // Consumers that build role-scoped detail routes at runtime pass
    // one of these as `basePath`; parameterised routes are then
    // built inline as `${ROUTE.UW}/application/${id}` etc.
    PORTAL: '/portal',
    QUOTE: '/quote',
    EAPP: '/eapp',
    UW: '/uw',
    /**
     * BLUF-1446 — Regional Manager module. Renamed from `MANAGER`
     * so the URL prefix spells out the role in full rather than
     * relying on a generic `/manager/*` that might collide with
     * future manager-adjacent roles (Product Manager, National
     * Manager, etc.).
     */
    REGIONAL_MANAGER: '/regional-manager',
    PRODUCT_OWNER: '/product-owner',
    NEW_BUSINESS: '/new-business',
    TPA_UW: '/tpa-uw',
    UW_ASSISTANT: '/uw-assistant',
    // BLUF-1543 — TELE_INTERVIEW retired. Tele Interviewer now
    // shares the /uw/* URL surface (see UW_DASHBOARD below). Any
    // legacy /tele-interview/* URL redirects to UW_DASHBOARD in
    // App.tsx.
    /**
     * Administrator module (BLUF-1404). Owned by the `Administrators`
     * Cognito role via the `admin-view` capability. Distinct from
     * `PRODUCT_OWNER` — Product Owners land on `/product-owner/*`
     * with the `product-owner-view` cap; the two URL trees are
     * disjoint and role-scoped.
     */
    ADMIN: '/admin',

    // ── Portal (/portal/*) — advisor-facing ─────────────────────
    PORTAL_DASHBOARD: '/portal/advisor-dashboard',
    PORTAL_CLIENTS: '/portal/clients',
    PORTAL_CLIENT_NEW: '/portal/clients/new',
    PORTAL_SELECT_CLIENT: '/portal/select-client',
    PORTAL_POLICY_HISTORY: '/portal/policy-history',
    PORTAL_POLICY_RENEWALS: '/portal/policy-renewals',
    PORTAL_COMMISSION_STATEMENTS: '/portal/commission-statements',

    // ── Quoter (/quote/*) ──────────────────────────────────────
    QUOTE_HOME: '/quote/quote',
    QUOTE_START: '/quote/start',
    QUOTE_COMPARE: '/quote/compare',
    QUOTE_NEEDS_ANALYSIS: '/quote/needs-analysis',
    QUOTE_NOTIFICATIONS: '/quote/notifications',
    QUOTE_OWNER_INFORMATION: '/quote/owner-information',

    // ── eApp (/eapp/*) ─────────────────────────────────────────
    EAPP_APPLICATION_HISTORY: '/eapp/application-history',
    EAPP_START_APPLICATION: '/eapp/start-application',
    EAPP_WIZARD: '/eapp/wizard',
    EAPP_COMPLIANCE: '/eapp/compliance',

    // ── UW (/uw/*) ─────────────────────────────────────────────
    UW_DASHBOARD: '/uw/underwriter-dashboard',
    UW_POLICY_SEARCH: '/uw/policy-search',
    UW_POLICY_RENEWALS: '/uw/policy-renewals',
    // BLUF-1516 — UW preview-only rate calc + illustration PDF. Shares
    // QuotePage/ComparePage with `previewOnly` + `sidebarVariant='uw'`.
    UW_QUICK_QUOTE: '/uw/quick-quote',
    UW_QUICK_QUOTE_COMPARE: '/uw/quick-quote-compare',
    /** Base for `${UW_POLICY_DETAIL}/${policyId}` from the UW renewal card grid. */
    UW_POLICY_DETAIL: '/uw/policy',

    // ── Product Owner (/product-owner/*) ───────────────────────
    // BLUF-1393 renamed `/admin/*` → `/product-owner/*`. The legacy
    // `/admin/*` path is preserved as a client-side redirect in
    // App.tsx that forwards to the equivalent /product-owner/* URL.
    // Do NOT add new `ADMIN_*` constants — always use the PO_* form.
    PO_ADVISOR_INSPECTOR: '/product-owner/advisor-inspector',
    PO_UNDERWRITING_REQUIREMENTS: '/product-owner/underwriting-requirements',
    PO_MULTI_PRODUCT_LIMITS: '/product-owner/multi-product-limits',
    PO_APPLICANT_CONFIGURATION: '/product-owner/applicant-configuration',
    PO_PRODUCTS: '/product-owner/products',
    PO_AGENCY_GROUPS: '/product-owner/agency-groups',
    PO_AGENCY_GROUP_NEW: '/product-owner/agency-groups/new',
    PO_USERS: '/product-owner/users',
    PO_USER_NEW: '/product-owner/users/new',
    PO_PLAN_VERSIONS: '/product-owner/plan-versions',
    PO_DOCUMENTS: '/product-owner/documents',

    // ── New Business (/new-business/*) — BLUF-1393 ─────────────
    NB_APPLICATION_SEARCH: '/new-business/application-search',
    NB_ADVISOR_MANAGER: '/new-business/advisor-manager',
    NB_CLUSTER_MANAGER: '/new-business/cluster-manager',
    NB_POLICY_ADMIN: '/new-business/policy-admin',
    NB_DASHBOARD: '/new-business/dashboard',

    // ── Manager / placeholder role dashboards ──────────────────
    // ── Regional Manager (/regional-manager/*) — BLUF-1446 ─────
    // Five flat nav items. Application Search is the landing.
    // Every page fetches through `/api/regional-manager/*`; server-
    // side Agency-Group scope is injected from the session, never
    // trusted from client params.
    REGIONAL_MANAGER_APPLICATION_SEARCH: '/regional-manager/application-search',
    REGIONAL_MANAGER_POLICY_SEARCH: '/regional-manager/policy-search',
    REGIONAL_MANAGER_POLICY_RENEWALS: '/regional-manager/policy-renewals',
    REGIONAL_MANAGER_ADVISOR_LIST: '/regional-manager/advisor-list',
    // BLUF-1073 — per-advisor drill-down. Sections rendered on this
    // page are gated by the advisor's cluster access flags
    // (sponsor_statement_access, sponsor_policy_and_application_access)
    // resolved server-side from Waypoint DynamoDB.
    REGIONAL_MANAGER_ADVISOR_DETAIL: '/regional-manager/advisor-detail',
    REGIONAL_MANAGER_COMMISSION_STATEMENTS: '/regional-manager/commission-statements',
    /** Base for `${REGIONAL_MANAGER_POLICY_DETAIL}/${policyId}` from the RM renewal card grid. */
    REGIONAL_MANAGER_POLICY_DETAIL: '/regional-manager/policy',
    // Cluster Manager scoped to the RM's assigned agency groups.
    // Server-side filter on GET /api/clusters returns only clusters
    // whose sponsor sits in the RM's producer set.
    REGIONAL_MANAGER_CLUSTER_MANAGER: '/regional-manager/cluster-manager',
    TPA_UW_DASHBOARD: '/tpa-uw/dashboard',
    UW_ASSISTANT_DASHBOARD: '/uw-assistant/dashboard',

    // ── Administrator (/admin/*) — BLUF-1404 ───────────────────
    // Real admin operations for the `Administrators` Cognito role.
    // Do NOT confuse with the legacy internal `Admin*` class names
    // used inside the Product Owner module — those are cosmetic
    // holdovers from the pre-BLUF-1393 rename.
    ADMIN_DASHBOARD: '/admin/dashboard',
    ADMIN_APPLICATION_TRANSFER: '/admin/application-transfer',
    // BLUF-1444 — 8 flat nav items for Internal Administrator role.
    // Application Search is the landing (auto-loads default results).
    // All pages read through /api/internal/* (BLUF-1444 Phase A).
    ADMIN_APPLICATION_SEARCH: '/admin/application-search',
    ADMIN_POLICY_SEARCH: '/admin/policy-search',
    ADMIN_AGENCY_GROUPS: '/admin/agency-groups',
    ADMIN_CLUSTER_MANAGER: '/admin/cluster-manager',
    ADMIN_ADVISOR_LIST: '/admin/advisor-list',
    ADMIN_COMMISSION_STATEMENTS: '/admin/commission-statements',

    // ── Claim Administrator (/claim-admin/*) — BLUF-1395 ───────
    // Single flat nav entry — read-only Policy Search over the whole
    // in-force policy book, no MGA / region / producer filter. Server
    // gate is `policies:read:all` at requireCap; client gate is
    // `claim-admin-view` on the RequireCapability wrapper in App.tsx.
    CLAIM_ADMIN: '/claim-admin',
    CLAIM_ADMIN_POLICY_SEARCH: '/claim-admin/policy-search',
} as const

/**
 * Discriminated route key type for callers that need to type a
 * route-name parameter (e.g. analytics events).
 */
export type RouteKey = keyof typeof ROUTE

/**
 * Backend endpoint constants for UI code. Same rule as `ROUTE`:
 * every `/api/*` literal introduced by a UI call site (`apiFetch`,
 * `apiPath` prop default, URL-template base) references one of these
 * keys instead of a bare string. Parameterised segments still get
 * built inline off the base.
 */
export const API_ROUTE = {
    // Advisor directory — RM caller returns agency-group-scoped list;
    // internal callers get the full list. Server-side scope is
    // authoritative — never trust a client-supplied producer id here.
    ADVISORS: '/api/advisors',

    // Commission statements — RM + Admin (internal) callers. Advisor
    // portal uses PORTAL_COMMISSION_STATEMENTS instead.
    COMMISSION_STATEMENTS: '/api/commission-statements',
    PORTAL_COMMISSION_STATEMENTS: '/api/portal/commission-statements',

    // Policy renewals — POST with { producerId } in body for every
    // non-advisor caller (UW / PO / NB / Admin / RM). Advisor portal
    // uses PORTAL_POLICIES_RENEWALS which reads session.producerId.
    POLICIES_RENEWALS: '/api/policies/renewals',
    PORTAL_POLICIES_RENEWALS: '/api/portal/policies/renewals',

    // BLUF-1476 — Quoter illustration email. POST body is a
    // QuoteRequest + optional `locale`; response is a `message/rfc822`
    // .eml the browser downloads and the OS opens as an editable draft.
    QUOTE_ILLUSTRATION_EMAIL: '/api/quote/illustration/email',

    // BLUF-1581 — Temporary Insurance (TIA) premium calculation. POST
    // body carries { application_id, product_code, face_amount,
    // adb_rider_face?, applicant_age, risk_class }; response is
    // { tia_premium, coverage_amount, effective_date, expiry_date }.
    // Proxies to PASS `/api/calculate/tia`; ADB exclusion happens
    // server-side.
    QUOTE_TIA_PREMIUM: '/api/quote/tia-premium',

    // BLUF-1602 — Multi-product TIA allocation. POST body carries
    // { application_id, applicant_age, risk_class, products: [{
    // product_id, plan_id, base_face_amount, wants_tia }] }; response
    // is { aggregate_cap_remaining, product_allocations: [{
    // product_id, plan_id, status, approved_coverage_amount,
    // tia_premium, status_message }] }. The server sorts by shortest-
    // term-first priority, walks the $1M cap, and calls PASS
    // `/api/calculate/tia` per eligible product with the allocated
    // coverage.
    QUOTE_TIA_ALLOCATE: '/api/quote/tia/allocate',

    // BLUF-1636 — base for per-application reads; UI callers build
    // `${API_ROUTE.APPLICATIONS_BASE}/${encodeURIComponent(id)}`.
    APPLICATIONS_BASE: '/api/applications',

    // BLUF-1625 — non-prod-only application purge endpoint used by
    // e2e specs to clean up applications they mint. The UI never
    // calls this — the constant lives here only to keep API_ROUTE ↔
    // API_PATH in sync per BLUF-1444. Parameter segment is inline
    // because this is an exception (e2e-only, never called from UI
    // code); prefer the base+interpolation pattern for real UI
    // callers.
    APPLICATIONS_PURGE: '/api/applications/:id/purge',

    // BLUF-1573 — mirror of API_PATH.APPLICATION_CREATE_JIRA. UI callers
    // build the URL off APPLICATIONS_BASE
    // (`${API_ROUTE.APPLICATIONS_BASE}/${id}/create-jira`); this entry
    // exists to keep API_ROUTE ↔ API_PATH in sync per BLUF-1444.
    APPLICATIONS_CREATE_JIRA: '/api/applications/:id/create-jira',

    // BLUF-1626 — eApp wizard base. Consumers build sub-routes off
    // this constant (`${API_ROUTE.EAPP_WIZARD_BASE}/${appId}/...`)
    // per the parameterised-route rule. Distinct from the SPA
    // `ROUTE.EAPP_WIZARD` nav path.
    EAPP_WIZARD_BASE: '/api/eapp/wizard',

    // BLUF-1626 — multi-life issuance-mode capture endpoint (GET + POST).
    // Mirrors API_PATH.EAPP_WIZARD_ISSUANCE_MODE on the API side (same
    // key, same value plus the /api prefix). Parameterised on
    // :applicationId — UI callers still build the URL via
    // `${API_ROUTE.EAPP_WIZARD_BASE}/${appId}/issuance-mode` per the
    // parameterised-route rule; this entry exists to keep the API_PATH
    // mirror complete.
    EAPP_WIZARD_ISSUANCE_MODE: '/api/eapp/wizard/:applicationId/issuance-mode',

    // BLUF-1665 — questionnaire API base. Consumers build sub-routes
    // off this constant (`${API_ROUTE.EAPP_QUESTIONNAIRE_BASE}/applications/${appId}/reset`)
    // per the parameterised-route rule, mirroring the EAPP_WIZARD_BASE
    // pattern above.
    EAPP_QUESTIONNAIRE_BASE: '/api/eapp/questionnaire',

    // BLUF-1665 — reset the questionnaire for an application: drops
    // every persisted answer + the metadata anchor, then re-pins
    // metadata to the caller's productId. NonmagnumQuestionnaireStep
    // fires this when the resolved questionnaire slug diverges from
    // `answerSet.metadata.productId` (application_type flipped Full ↔
    // Quick mid-flow). Parameterised on :applicationId — UI callers
    // still build the URL via `${API_ROUTE.EAPP_QUESTIONNAIRE_BASE}/applications/${appId}/reset`
    // per the parameterised-route rule; this entry mirrors
    // `API_PATH.EAPP_QUESTIONNAIRE_APPLICATION_RESET` on the API side
    // so a renamed endpoint is greppable across both packages.
    EAPP_QUESTIONNAIRE_APPLICATION_RESET:
        '/api/eapp/questionnaire/applications/:applicationId/reset',

    // BLUF-1617 — TIA defaults surface. Consumed by the Product-Owner
    // admin page (GET + PATCH) and by the wizard's ineligible
    // multi-life card that needs `messages.ineligible_no_product`
    // without triggering an allocation call.
    TIA_CONFIG: '/api/tia-config',

    // BLUF-1617 — Product Editor admin surfaces referenced by the TIA
    // Config page's product/plan dropdowns.
    //   PRODUCTS_CATALOGUE — list of every product (active + inactive).
    //   PRODUCTS_BASE — prefix for per-product sub-routes; consumers
    //                   build `${API_ROUTE.PRODUCTS_BASE}/${id}/edit`,
    //                   `.../refresh`, `.../activation-check`, etc.
    //                   (the SPA `ROUTE.PO_PRODUCTS` is the nav path,
    //                   this is the API path).
    PRODUCTS_CATALOGUE: '/api/products/catalogue',
    PRODUCTS_BASE: '/api/products',

    // BLUF-1648 — Advisor licence management. CRUD routes nest under
    // ADVISORS (e.g. `${API_ROUTE.ADVISORS}/${id}/licenses`); the
    // batch status and licence-validation endpoints have their own
    // keys because they live at distinct sub-routes.
    ADVISORS_LICENSE_STATUS: '/api/advisors/license-status',
    APPLICATIONS_LICENCE_VALIDATION: '/api/applications',
} as const

export type ApiRouteKey = keyof typeof API_ROUTE
